import type { Guide } from "../types";

// API & Integrations — the public REST API (/api/v1) and webhooks, for IT teams,
// integrators and building-management systems. Linked from Account → Developer
// in the app. Keep in step with apps/api/src/public-api (openapi.ts is the
// machine-readable source of truth) and apps/api/src/webhooks.

const BASE = "https://app.pulsepanels.com/api/v1";
const UPDATED = "2026-10-02";

const SCREEN_EXAMPLE = `{
  "id": "cm1x8k2p40001",
  "externalId": "L3-LOBBY-01",
  "name": "Lobby left",
  "workspaceId": "cm0ws9a1b0002",
  "online": true,
  "orientation": "landscape",
  "timezone": "Asia/Qatar",
  "lastSeenAt": "2026-10-02T08:14:55.000Z",
  "pairedAt": "2026-09-12T06:30:00.000Z",
  "groupId": null,
  "content": { "type": "PLAYLIST", "id": "cm1pl2c3d0007", "name": "Lobby loop" }
}`;

const PAGE_LOOP_NODE = `// Node.js 18+ — every screen, page by page
const BASE = "${BASE}";
const headers = { Authorization: \`Bearer \${process.env.PULSEPANELS_API_KEY}\` };

async function allScreens() {
  const screens = [];
  let cursor = "";
  do {
    const url = \`\${BASE}/screens?limit=500\${cursor ? \`&cursor=\${cursor}\` : ""}\`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error((await res.json()).error.message);
    const page = await res.json();
    screens.push(...page.data);
    cursor = page.pagination.hasMore ? page.pagination.nextCursor : "";
  } while (cursor);
  return screens;
}`;

const PAGE_LOOP_PY = `# Python 3 with requests — every screen, page by page
import os, requests

BASE = "${BASE}"
session = requests.Session()
session.headers["Authorization"] = f"Bearer {os.environ['PULSEPANELS_API_KEY']}"

def all_screens():
    screens, params = [], {"limit": 500}
    while True:
        page = session.get(f"{BASE}/screens", params=params, timeout=15).json()
        screens += page["data"]
        if not page["pagination"]["hasMore"]:
            return screens
        params["cursor"] = page["pagination"]["nextCursor"]`;

const VERIFY_NODE = `// Node.js (Express) — verify, answer fast, then do the work
const express = require("express");
const crypto = require("crypto");
const SECRET = process.env.PULSEPANELS_WEBHOOK_SECRET; // whsec_…

const app = express();
app.post("/hooks/pulsepanels", express.raw({ type: "application/json" }), (req, res) => {
  const id = req.get("webhook-id");
  const ts = req.get("webhook-timestamp");
  const body = req.body.toString("utf8"); // the RAW body, before any JSON parsing
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return res.sendStatus(400);

  const key = Buffer.from(SECRET.replace(/^whsec_/, ""), "base64");
  const expected = crypto.createHmac("sha256", key).update(\`\${id}.\${ts}.\${body}\`).digest("base64");
  const ok = req.get("webhook-signature").split(" ").some((s) => s === \`v1,\${expected}\`);
  if (!ok) return res.sendStatus(401);

  res.sendStatus(204);
  handleEvent(JSON.parse(body)); // e.g. queue it; dedupe on the webhook-id
});`;

const VERIFY_PY = `# Python 3 — returns True when the delivery really came from PulsePanels
import base64, hashlib, hmac, time

def verify(secret: str, headers: dict, raw_body: bytes) -> bool:
    msg_id = headers["webhook-id"]
    ts = headers["webhook-timestamp"]
    if abs(time.time() - int(ts)) > 300:          # replay window
        return False
    key = base64.b64decode(secret.removeprefix("whsec_"))
    signed = f"{msg_id}.{ts}.".encode() + raw_body
    expected = base64.b64encode(hmac.new(key, signed, hashlib.sha256).digest()).decode()
    return any(
        hmac.compare_digest(sig[3:], expected)
        for sig in headers["webhook-signature"].split(" ")
        if sig.startswith("v1,")
    )`;

const VERIFY_CS = `// C# (.NET 6+)
using System.Security.Cryptography;
using System.Text;

static bool Verify(string secret, string id, string timestamp, string rawBody, string signatureHeader)
{
    var age = DateTimeOffset.UtcNow.ToUnixTimeSeconds() - long.Parse(timestamp);
    if (Math.Abs(age) > 300) return false;                       // replay window
    var key = Convert.FromBase64String(secret.StartsWith("whsec_") ? secret[6..] : secret);
    using var hmac = new HMACSHA256(key);
    var mac = hmac.ComputeHash(Encoding.UTF8.GetBytes($"{id}.{timestamp}.{rawBody}"));
    var expected = "v1," + Convert.ToBase64String(mac);
    return signatureHeader.Split(' ').Any(s => CryptographicOperations.FixedTimeEquals(
        Encoding.UTF8.GetBytes(s), Encoding.UTF8.GetBytes(expected)));
}`;

const BMS_POLLER = `# Python 3 — push screen status into a BMS every minute
# Each screen's External ID is its BMS point name (e.g. "L3-LOBBY-01").
import os, time, requests

BASE = "${BASE}"
session = requests.Session()
session.headers["Authorization"] = f"Bearer {os.environ['PULSEPANELS_API_KEY']}"

def write_bms_point(point: str, online: bool):
    ...  # your BMS / BACnet / Modbus / MQTT client goes here

while True:
    params = {"limit": 500}
    while True:
        page = session.get(f"{BASE}/screens", params=params, timeout=15).json()
        for s in page["data"]:
            if s["externalId"]:
                write_bms_point(s["externalId"], s["online"])
        if not page["pagination"]["hasMore"]:
            break
        params["cursor"] = page["pagination"]["nextCursor"]
    time.sleep(60)`;

const FIRE_ALARM = `# Python 3 — fire alarm → evacuation message on every screen, and back
import os, requests

BASE = "${BASE}"
H = {"Authorization": f"Bearer {os.environ['PULSEPANELS_API_KEY']}"}

def on_fire_alarm(alarm_id: str):
    r = requests.post(f"{BASE}/emergency/alerts", timeout=10, headers={
        **H, "Idempotency-Key": f"fire-{alarm_id}",   # a retry never sends twice
    }, json={"template": "fire", "target": {"all": True}})
    r.raise_for_status()
    return r.json()["alertId"]                         # keep it to stop the alert

def on_alarm_reset(alert_id: str):
    requests.post(f"{BASE}/emergency/alerts/{alert_id}/stop", headers=H, timeout=10).raise_for_status()`;

export const developerGuides: Guide[] = [
  // ── Get started ────────────────────────────────────────────────────────────
  {
    slug: "api-overview",
    category: "developers",
    route: "/developers/api-overview",
    group: "Get started",
    title: "API overview & quick start",
    summary: "What the PulsePanels API can do, and your first calls in five minutes.",
    intro:
      "The PulsePanels REST API lets your own systems read your screens and content library: a building-management system, an IT dashboard, a ticketing tool or a script. Webhooks push screen events to you as they happen. Everything is JSON over HTTPS.",
    prerequisites: [
      "The API & integrations add-on on your organization. If the Developer page says it is an add-on, ask your PulsePanels provider to enable it.",
      "An API key. The Owner or an Admin creates one under Account Settings → Developer.",
      "A tool that can send HTTPS requests: curl, Postman, or any programming language.",
    ],
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "What you can do",
        blocks: [
          {
            type: "table",
            head: ["You want to…", "Use"],
            rows: [
              ["See which screens are online and what they're playing", "`GET /v1/screens`"],
              ["Look a screen up by your own ID, such as a BMS point name", "`GET /v1/screens/ext:<your-id>`"],
              ["Read your channels, playlists, media and apps", "`GET /v1/channels`, `/v1/playlists`, `/v1/media`, `/v1/apps`"],
              ["Check a screen's health or see its last screenshot", "`GET /v1/screens/{id}/health`, `/v1/screens/{id}/screenshot`"],
              ["Read your screen groups, locations and Spaces", "`GET /v1/groups`, `/v1/locations`, `/v1/spaces`"],
              ["Pull proof-of-play and uptime figures", "`GET /v1/reports/proof-of-play`, `/v1/screens/{id}/uptime`"],
              ["See which emergency alerts are on screens", "`GET /v1/emergency/alerts`"],
              ["Send or stop an emergency alert from another system", "`POST /v1/emergency/alerts`, `…/stop`"],
              ["Reload, restart or reboot screens; set volume and brightness", "`POST /v1/screens/{id}/commands`, `PATCH /v1/screens/{id}`"],
              ["Change what a screen or screen group plays", "`PUT /v1/screens/{id}/content`, `PUT /v1/groups/{id}/content`"],
              ["Push room bookings to room displays", "`PUT /v1/locations/{id}/events/ext:{bookingId}`"],
              ["Drive clinic door screens from a hospital system", "`PUT /v1/clinics/ext:{roomId}/visits/ext:{appointmentId}`, `…/call`"],
              ["Be told the moment a screen goes offline or comes back", "Webhooks: `screen.offline`, `screen.online`"],
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Reading is safe to start with: new keys can only read. Changing what's on screens needs write scopes you tick deliberately for each key.",
          },
        ],
      },
      {
        title: "Find your base URL",
        blocks: [
          { type: "p", text: `Every endpoint lives under \`${BASE}\`. The Developer page in the app shows the exact base URL for your account. On an on-premises server it is that server's address followed by \`/api/v1\`.` },
          {
            type: "p",
            text: "Click OpenAPI spec on the Developer page to download the full machine-readable reference. Postman, Insomnia and most code generators import it directly.",
          },
        ],
      },
      {
        title: "Check your key",
        blocks: [
          { type: "p", text: "Call `/v1/ping` with your key. It needs no scope and tells you which organization the key belongs to and what it may do." },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl ${BASE}/ping \\\n  -H "Authorization: Bearer pp_live_your_key"`,
          },
          {
            type: "code",
            title: "Response",
            lang: "json",
            code: `{
  "ok": true,
  "organizationId": "cm0org1a2b0001",
  "key": {
    "id": "cm1key3c4d0005",
    "name": "Building management system",
    "scopes": ["screens:read"],
    "workspaceId": null
  },
  "ts": "2026-10-02T08:15:00.000Z"
}`,
          },
        ],
      },
      {
        title: "List the screens that are offline",
        blocks: [
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl "${BASE}/screens?online=false" \\\n  -H "Authorization: Bearer pp_live_your_key"`,
          },
          {
            type: "code",
            title: "Response",
            lang: "json",
            code: `{
  "data": [
    {
      "id": "cm1x8k2p40001",
      "externalId": "L3-LOBBY-01",
      "name": "Lobby left",
      "online": false,
      "lastSeenAt": "2026-10-02T07:41:09.000Z",
      "...": "…"
    }
  ],
  "pagination": { "limit": 100, "nextCursor": null, "hasMore": false }
}`,
          },
          { type: "steps-ref", label: "Every field of a screen", to: "/developers/screens-api" },
        ],
      },
      {
        title: "Where to go next",
        blocks: [
          { type: "steps-ref", label: "Authentication, keys & scopes", to: "/developers/authentication" },
          { type: "steps-ref", label: "Pagination, errors & rate limits", to: "/developers/conventions" },
          { type: "steps-ref", label: "Webhooks: events, signatures & retries", to: "/developers/webhooks" },
          { type: "steps-ref", label: "Connect a building-management system", to: "/developers/connect-a-bms" },
        ],
      },
    ],
    related: [
      { label: "Developer: API keys & webhooks", to: "/account/developer" },
      { label: "Authentication, keys & scopes", to: "/developers/authentication" },
    ],
    keywords: ["api", "rest", "developer", "integration", "quick start", "curl", "openapi", "postman", "base url"],
  },

  {
    slug: "authentication",
    category: "developers",
    route: "/developers/authentication",
    group: "Get started",
    title: "Authentication, keys & scopes",
    summary: "Send your API key, choose what it may read, and lock it to a Space, an IP address or an expiry date.",
    intro:
      "Every request carries an API key. A key belongs to your organization (not to a person), so it keeps working when people come and go. Give each system its own key with only the access it needs.",
    prerequisites: ["You are the Owner or an Admin of the account (only they can create keys)."],
    readingTime: 5,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Send the key on every request",
        blocks: [
          { type: "p", text: "Use the `Authorization` header with the Bearer scheme, or the `X-API-Key` header. Both work the same way." },
          {
            type: "code",
            title: "Either header",
            lang: "http",
            code: `GET /api/v1/screens HTTP/1.1
Host: app.pulsepanels.com
Authorization: Bearer pp_live_0123456789abcdef0123456789abcdef01234567

GET /api/v1/screens HTTP/1.1
Host: app.pulsepanels.com
X-API-Key: pp_live_0123456789abcdef0123456789abcdef01234567`,
          },
          {
            type: "note",
            tone: "warning",
            text: "Call the API from a server, never from a web page or an app on someone's phone: anyone who can see the page can copy the key. Store it in your secrets manager or an environment variable.",
          },
        ],
      },
      {
        title: "Choose the key's scopes",
        blocks: [
          { type: "p", text: "Scopes decide what a key may read. Tick them under Access when you create or edit the key." },
          {
            type: "table",
            head: ["Scope", "Lets the key"],
            rows: [
              ["`screens:read`", "List screens and read their status, timezone, group and what they're playing"],
              ["`content:read`", "Read channels, playlists, media files (with download links) and apps"],
              ["`reports:read`", "Read proof-of-play and uptime reports"],
              ["`emergency:read`", "Read emergency alerts, whether each screen displayed them, and templates"],
              ["`screens:control`", "Send commands (reload, restart, reboot), set volume and brightness, request a screenshot"],
              ["`content:publish`", "Change what screens and screen groups play, and publish it"],
              ["`events:write`", "Create, update and delete bookings on locations"],
              ["`emergency:trigger`", "Send and stop emergency alerts"],
              ["`data:read`", "Read the live data values your systems have sent"],
              ["`data:write`", "Send live data values (temperatures, counts, statuses) for screens to show"],
              ["`triggers:fire`", "Fire and stop the triggers set up on the Developer page"],
              ["`clinics:read`", "Read clinics, doctor sessions, the day's visits (ticket numbers only) and what each clinic door shows"],
              ["`clinics:write`", "Send clinics, doctor sessions and visits from a hospital system, call patients and override a clinic door"],
            ],
          },
          {
            type: "p",
            text: "`/v1/ping` needs no scope. Calling an endpoint without its scope returns `403` with the code `insufficient_scope` and the missing scope in `requiredScope`.",
          },
          {
            type: "note",
            tone: "info",
            text: "Scopes that change what people see (`screens:control`, `content:publish`, `events:write`, `emergency:trigger`, `data:write`, `triggers:fire`, `clinics:write`) are never ticked by default and older keys never get them. Keys created before scopes existed keep every read scope. The clinic scopes only appear for organisations with the Clinic Flow module.",
          },
        ],
      },
      {
        title: "Limit a key to one Space",
        blocks: [
          {
            type: "p",
            text: "If your account has several Spaces, pick one under Space when you create the key. The key then sees only that Space's screens and content, plus media shared with every Space. Requests for anything else return `404`, as if it didn't exist.",
          },
          {
            type: "p",
            text: "A key for all Spaces can still narrow a list with `?workspaceId=`. A Space-limited key that asks for another Space gets `403 space_not_allowed`. If its Space is archived, the key stops working with `403 space_archived`.",
          },
        ],
      },
      {
        title: "Allow only your server's IP address",
        blocks: [
          {
            type: "p",
            text: "Under Allowed IP addresses, enter the public address your system calls from, one per line. Ranges in CIDR form work too, for example `203.0.113.0/24`. Requests from any other address get `403 ip_not_allowed`, so a leaked key is useless elsewhere.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Use the address the internet sees, not your server's internal one. If you are not sure, ask your network team for the outbound (NAT) address of the system.",
          },
        ],
      },
      {
        title: "Expire, rotate and revoke keys",
        blocks: [
          {
            type: "ul",
            items: [
              "Expires: choose 30 days, 90 days or 1 year. After that the key returns `401 api_key_expired`.",
              "To rotate a key without downtime: create a new key, switch your system to it, check its Last used time moves, then revoke the old one.",
              "Revoke stops a key immediately. It then returns `401 invalid_api_key`.",
              "If an organization's account is suspended, its keys return `403 organization_suspended` until it is reactivated.",
            ],
          },
        ],
      },
      {
        title: "Authentication errors at a glance",
        blocks: [
          {
            type: "table",
            head: ["Status", "Code", "What to do"],
            rows: [
              ["401", "`missing_api_key`", "Send the key in `Authorization: Bearer …` or `X-API-Key`."],
              ["401", "`invalid_api_key`", "The key is wrong or revoked. Check for copy errors, or create a new key."],
              ["401", "`api_key_expired`", "Create a new key."],
              ["403", "`insufficient_scope`", "Edit the key and tick the scope named in `requiredScope`."],
              ["403", "`ip_not_allowed`", "Add your server's public IP address to the key, or call from an allowed address."],
              ["403", "`space_not_allowed` / `space_archived`", "The key is limited to another Space, or its Space was archived."],
              ["403", "`organization_suspended`", "The account is suspended. Contact your provider."],
              ["403", "`api_not_enabled`", "The API & integrations add-on isn't enabled for your organization. Ask your provider. Stopping an alert or trigger still works."],
              ["429", "`too_many_failed_attempts`", "Too many invalid keys from your address in one minute. Fix the key and wait a minute."],
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Developer: API keys & webhooks", to: "/account/developer" },
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
    ],
    keywords: ["api key", "bearer", "x-api-key", "scope", "scopes", "ip allowlist", "cidr", "expiry", "revoke", "rotate", "space", "401", "403"],
  },

  {
    slug: "conventions",
    category: "developers",
    route: "/developers/conventions",
    group: "Get started",
    title: "Pagination, errors & rate limits",
    summary: "How lists page, how to sync only what changed, what errors look like, and how many calls you can make.",
    intro: "These rules apply to every endpoint, so an integration written once keeps working as new endpoints arrive.",
    readingTime: 6,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Requests and responses",
        blocks: [
          {
            type: "ul",
            items: [
              "HTTPS only. Responses are JSON in UTF-8.",
              "Times are ISO 8601 in UTC, for example `2026-10-02T08:15:00.000Z`.",
              "IDs are opaque strings. Store them as text and don't parse them.",
              "New fields may appear in responses at any time. Ignore fields you don't know.",
            ],
          },
        ],
      },
      {
        title: "Page through lists",
        blocks: [
          {
            type: "p",
            text: "Lists return `{ data, pagination }`, oldest first. Pass `nextCursor` back as `?cursor=` until `hasMore` is `false`. `limit` is 100 by default, up to 500.",
          },
          {
            type: "code",
            title: "Page 1, then page 2",
            lang: "bash",
            code: `curl "${BASE}/screens?limit=2" -H "X-API-Key: pp_live_your_key"
# → "pagination": { "limit": 2, "nextCursor": "Y20xeDhrMnA0MDAwMg", "hasMore": true }

curl "${BASE}/screens?limit=2&cursor=Y20xeDhrMnA0MDAwMg" -H "X-API-Key: pp_live_your_key"`,
          },
          { type: "code", title: "Node.js", lang: "javascript", code: PAGE_LOOP_NODE },
          { type: "code", title: "Python", lang: "python", code: PAGE_LOOP_PY },
          {
            type: "note",
            tone: "info",
            text: "A cursor is only good for the list and filters it came from. Don't build cursors yourself; their format can change.",
          },
        ],
      },
      {
        title: "Sync only what changed",
        blocks: [
          {
            type: "p",
            text: "Every list accepts `?updatedSince=` with an ISO 8601 time. Remember when your last sync started and pass it next time to fetch only rows created or changed since.",
          },
          { type: "code", lang: "bash", title: "Request", code: `curl "${BASE}/media?updatedSince=2026-10-01T00:00:00Z" -H "X-API-Key: pp_live_your_key"` },
          {
            type: "note",
            tone: "tip",
            text: "A screen's `online` value comes from its heartbeat, so `updatedSince` doesn't reliably pick up online/offline changes. To follow those, use webhooks or filter with `?online=false`.",
          },
        ],
      },
      {
        title: "Handle errors",
        blocks: [
          { type: "p", text: "Every error has the same shape. Branch on `code`, which never changes; `message` is for people and may be reworded." },
          {
            type: "code",
            title: "Error",
            lang: "json",
            code: `{
  "error": {
    "code": "insufficient_scope",
    "message": "This API key lacks the \\"screens:read\\" scope.",
    "status": 403,
    "requestId": "req_5dfe7d0782d578eaf074a78d",
    "requiredScope": "screens:read"
  }
}`,
          },
          {
            type: "table",
            head: ["Status", "Code", "Meaning"],
            rows: [
              ["400", "`invalid_limit`, `invalid_cursor`, `invalid_updated_since`, `invalid_online`, `invalid_active`, `invalid_status`, `invalid_range`, `range_too_long`", "A query parameter is not valid. The message says which."],
              ["401 / 403", "see Authentication", "The key is missing, invalid, expired, or not allowed to do this."],
              ["404", "`not_found`", "No such item for this key (wrong ID, deleted, or outside the key's Space)."],
              ["404", "`no_screenshot`", "The screen has no captured screenshot yet."],
              ["400", "`invalid_body`, `invalid_target`, `invalid_command`, `unknown_template`, `no_target_screens`, `invalid_event_ref`", "The request body is not valid. The message says what to fix."],
              ["404", "`unknown_target`, `content_not_found`", "A screen, group, location or content id in the body doesn't exist for this key. `missing` lists them."],
              ["403", "`license_paused`", "Your licence is paused, so content can't be published."],
              ["409", "`not_api_event`", "That event came from a calendar or the app, so the API can't change it."],
              ["429", "`rate_limited`", "Slow down: wait for `Retry-After` seconds."],
              ["500", "`internal_error`", "Something failed on our side. Retry later; quote the `requestId` to support if it persists."],
            ],
          },
        ],
      },
      {
        title: "Stay inside the rate limit",
        blocks: [
          {
            type: "p",
            text: "Each key may make 300 requests per minute. Every response tells you where you stand:",
          },
          {
            type: "table",
            head: ["Header", "Meaning"],
            rows: [
              ["`X-RateLimit-Limit`", "Requests allowed per minute for this key."],
              ["`X-RateLimit-Remaining`", "Requests left in the current minute."],
              ["`X-RateLimit-Reset`", "When the minute resets, in Unix seconds."],
              ["`Retry-After`", "On a `429` only: seconds to wait before trying again."],
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Polling every screen once a minute takes one request per 500 screens. Prefer webhooks for online/offline changes, and use `updatedSince` for content syncs.",
          },
        ],
      },
      {
        title: "Quote the request ID",
        blocks: [
          {
            type: "p",
            text: "Every response has an `X-Request-Id` header, also included in error bodies. Quote it when you contact support so we can find the call. Send your own `X-Request-Id` (8–64 letters, digits, dots, colons, dashes or underscores) and we use it, so one ID spans your logs and ours.",
          },
        ],
      },
      {
        title: "Retry writes safely with Idempotency-Key",
        blocks: [
          {
            type: "p",
            text: "Endpoints that change things accept an `Idempotency-Key` header: any unique string, such as a UUID. If a request times out and you send it again with the same key, you get the first result back instead of the action happening twice. Keys are remembered for 24 hours.",
          },
          {
            type: "note",
            tone: "info",
            text: "Use it on every write that a retry could repeat, especially `POST /v1/emergency/alerts`: the same key twice returns the first alert instead of sending a second one. Reusing a key for a different request returns `422 idempotency_key_reused`.",
          },
        ],
      },
      {
        title: "Versions and deprecation",
        blocks: [
          {
            type: "p",
            text: "`/v1` only changes in ways that don't break existing code: new endpoints, new optional parameters, new response fields. When something is replaced, it keeps working and its responses carry a `Deprecation: true` header with a `Link` to the successor.",
          },
          {
            type: "p",
            text: "Deprecated today: `GET /v1/content`, which returns the whole library in one call (cut off at 500 per type, flagged in `truncated`). Use the per-type lists instead.",
          },
        ],
      },
    ],
    related: [
      { label: "Authentication, keys & scopes", to: "/developers/authentication" },
      { label: "Screens API", to: "/developers/screens-api" },
    ],
    keywords: ["pagination", "cursor", "nextCursor", "limit", "updatedSince", "errors", "error codes", "rate limit", "429", "retry-after", "request id", "idempotency", "deprecation"],
  },

  // ── Reference ──────────────────────────────────────────────────────────────
  {
    slug: "screens-api",
    category: "developers",
    route: "/developers/screens-api",
    group: "Reference",
    title: "Screens API",
    summary: "List screens with their online status and current content, and look one up by its ID or your own External ID.",
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    prerequisites: ["A key with the `screens:read` scope."],
    steps: [
      {
        title: "List screens — GET /v1/screens",
        blocks: [
          {
            type: "table",
            head: ["Query parameter", "Description"],
            rows: [
              ["`online`", "`true` for screens heard from in the last 90 seconds, `false` for the rest (including never connected)."],
              ["`groupId`", "Only screens in this screen group."],
              ["`externalId`", "Only the screen with this External ID."],
              ["`workspaceId`", "Only screens in this Space."],
              ["`updatedSince`", "Only screens whose name, settings or content changed since this time. Not reliable for online/offline: use `online` or webhooks for that."],
              ["`limit`, `cursor`", "Paging. See Pagination, errors & rate limits."],
            ],
          },
          { type: "code", title: "Request", lang: "bash", code: `curl "${BASE}/screens?online=true&limit=50" -H "X-API-Key: pp_live_your_key"` },
        ],
      },
      {
        title: "The screen object",
        blocks: [
          { type: "code", title: "Screen", lang: "json", code: SCREEN_EXAMPLE },
          {
            type: "table",
            head: ["Field", "Description"],
            rows: [
              ["`id`", "Our ID for the screen."],
              ["`externalId`", "Your own ID for the screen, or `null`. See below."],
              ["`name`", "The screen's name in PulsePanels."],
              ["`location`", "The physical location set on the screen, or `null`."],
              ["`workspaceId`", "The Space it belongs to."],
              ["`online`", "Heard from in the last 90 seconds."],
              ["`orientation`", "`landscape` or `portrait` (also `_flipped` variants)."],
              ["`timezone`", "The screen's timezone, or `null` to follow the account."],
              ["`lastSeenAt`", "Last heartbeat, or `null` if it never connected."],
              ["`pairedAt`", "When it was paired."],
              ["`groupId`", "Its screen group, or `null`."],
              ["`content`", "What it plays: `type` (`CHANNEL`, `PLAYLIST`, `MEDIA` or `APP`), `id` and `name`. `null` when nothing is published."],
            ],
          },
        ],
      },
      {
        title: "Get one screen — GET /v1/screens/{id}",
        blocks: [
          { type: "p", text: "Use our ID, or your External ID prefixed with `ext:`." },
          {
            type: "code",
            title: "By ID or by External ID",
            lang: "bash",
            code: `curl ${BASE}/screens/cm1x8k2p40001 -H "X-API-Key: pp_live_your_key"
curl ${BASE}/screens/ext:L3-LOBBY-01 -H "X-API-Key: pp_live_your_key"`,
          },
        ],
      },
      {
        title: "Check a screen's health — GET /v1/screens/{id}/health",
        blocks: [
          {
            type: "p",
            text: "Returns what the player last reported: `appVersion`, `os`, `resolution`, `cpuPercent`, `memoryPercent`, `diskPercent`, `networkKbps`, `appUptimeSeconds` (how long the player app has run), and `network` (interface, local IP and MAC address). `reportedAt` says when. Fields are `null` until the player has reported.",
          },
        ],
      },
      {
        title: "Get the last screenshot — GET /v1/screens/{id}/screenshot",
        blocks: [
          {
            type: "p",
            text: "Returns the last captured frame as a JPEG image, with its capture time in the `X-Captured-At` header. Screenshots are captured while someone watches the screen's live preview in the app, so the image can be old. A screen that has never been previewed returns `404 no_screenshot`.",
          },
          { type: "code", title: "Save it to a file", lang: "bash", code: `curl -D - -o lobby.jpg ${BASE}/screens/ext:L3-LOBBY-01/screenshot -H "X-API-Key: pp_live_your_key"` },
        ],
      },
      {
        title: "Give screens your own IDs",
        blocks: [
          {
            type: "ol",
            items: [
              "In PulsePanels, open Screens and click the screen.",
              "In its settings, find the Location & ID card and fill in External ID, for example the BMS point name or asset tag.",
              "Click Save.",
            ],
          },
          {
            type: "p",
            text: "An External ID can use letters, digits and `. _ : / -`, up to 100 characters. It must be unique within your organization; saving a duplicate is refused.",
          },
        ],
      },
    ],
    related: [
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
      { label: "Webhooks: events, signatures & retries", to: "/developers/webhooks" },
    ],
    keywords: ["screens", "screen status", "online", "offline", "external id", "externalId", "ext:", "lastSeenAt", "now playing"],
  },

  {
    slug: "content-api",
    category: "developers",
    route: "/developers/content-api",
    group: "Reference",
    title: "Content library API",
    summary: "Read your channels, playlists, media files and apps, and fetch any one of them in detail.",
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    prerequisites: ["A key with the `content:read` scope."],
    steps: [
      {
        title: "List each type",
        blocks: [
          {
            type: "table",
            head: ["Endpoint", "Returns", "Extra filter"],
            rows: [
              ["`GET /v1/channels`", "id, name, orientation, workspaceId, published, publishedAt", "—"],
              ["`GET /v1/playlists`", "id, name, orientation, workspaceId, itemCount", "—"],
              ["`GET /v1/media`", "id, name, type, mimeType, sizeBytes, durationMs, fileUrl", "`type`: `image`, `video`, `document` or `audio`"],
              ["`GET /v1/apps`", "id, name, type, workspaceId", "`type`: the app type"],
            ],
          },
          {
            type: "p",
            text: "All lists page, accept `updatedSince` and `workspaceId`, and include `createdAt` and `updatedAt`. Archived items are left out.",
          },
        ],
      },
      {
        title: "Get a playlist — GET /v1/playlists/{id}",
        blocks: [
          { type: "p", text: "Returns the playlist with its items in play order. Each item's `content` is a media file (with a download `fileUrl`) or an app." },
          {
            type: "code",
            title: "Response (shortened)",
            lang: "json",
            code: `{
  "id": "cm1pl2c3d0007",
  "name": "Lobby loop",
  "itemCount": 2,
  "totalDurationMs": 25000,
  "items": [
    { "order": 0, "durationMs": 10000,
      "content": { "kind": "MEDIA", "id": "cm1md4e5f0009", "name": "Welcome.mp4", "type": "video",
                   "fileUrl": "https://app.pulsepanels.com/api/media-files/abc123.mp4" } },
    { "order": 1, "durationMs": 15000,
      "content": { "kind": "APP", "id": "cm1ap6g7h0011", "name": "Doha weather", "type": "WEATHER" } }
  ]
}`,
          },
        ],
      },
      {
        title: "Get a channel — GET /v1/channels/{id}",
        blocks: [
          {
            type: "p",
            text: "Returns the layout as screens play it: `size`, `zones` (each with its geometry and items) and rotating `scenes`. `source` says whether that is the `published` channel or, for a channel never published, the `draft`.",
          },
        ],
      },
      {
        title: "Get a media file or an app",
        blocks: [
          {
            type: "ul",
            items: [
              "`GET /v1/media/{id}` returns the file's details and `fileUrl`, a direct download link. Documents also list `pageUrls`, one image per page.",
              "`GET /v1/apps/{id}` returns the app's settings in `config`. Values that look secret (passwords, tokens, keys) are replaced with `[redacted]` and listed in `redactedKeys`.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
      { label: "Screens API", to: "/developers/screens-api" },
    ],
    keywords: ["content", "channels", "playlists", "media", "apps", "fileUrl", "download", "zones", "library"],
  },

{
    slug: "organization-api",
    category: "developers",
    route: "/developers/organization-api",
    group: "Reference",
    title: "Spaces, screen groups & locations API",
    summary: "Read how your screens are organised: Spaces, screen groups and locations, each with its screens, by our ID or yours.",
    intro:
      "Integrations usually think in zones and rooms, not single screens. These endpoints return your Spaces, your screen groups (a floor, a wing, a video wall) and your locations (meeting rooms and other places used by event boards), each with the screens in it.",
    prerequisites: ["A key with the `screens:read` scope."],
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "List Spaces — GET /v1/spaces",
        blocks: [
          { type: "p", text: "Returns your active Spaces: `id`, `name`, `slug`, `description` and `timezone`. A key limited to one Space only sees that Space." },
        ],
      },
      {
        title: "List screen groups — GET /v1/groups",
        blocks: [
          {
            type: "p",
            text: "Screen groups are the groups on the Screen Groups page: screens that play the same content. Lists page like every other list and accept `workspaceId`, `externalId` and `updatedSince`.",
          },
          {
            type: "table",
            head: ["Field", "Description"],
            rows: [
              ["`id`, `externalId`", "Our ID, and your own ID for the group (or `null`)."],
              ["`name`, `description`", "As set in the app."],
              ["`orientation`, `timezone`", "The group's orientation and timezone."],
              ["`syncPlayback`", "`true` when the group's screens play in sync."],
              ["`screenCount`", "How many screens are in the group."],
              ["`content`", "What the group plays: `type`, `id` and `name`, or `null`."],
            ],
          },
        ],
      },
      {
        title: "Get one group with its screens — GET /v1/groups/{id}",
        blocks: [
          { type: "p", text: "Use our ID or your External ID prefixed with `ext:`. The response adds `screens`: each screen's `id`, `externalId`, `name`, `online` and `lastSeenAt`." },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl ${BASE}/groups/ext:L3-EAST -H "X-API-Key: pp_live_your_key"`,
          },
          {
            type: "code",
            title: "Response (shortened)",
            lang: "json",
            code: `{
  "id": "cm1gr0up00001",
  "externalId": "L3-EAST",
  "name": "Level 3 east wing",
  "screenCount": 2,
  "content": { "type": "CHANNEL", "id": "cm1ch4n00003", "name": "Level 3 news" },
  "screens": [
    { "id": "cm1x8k2p40001", "externalId": "L3-LOBBY-01", "name": "Lobby left", "online": true, "lastSeenAt": "2026-10-02T08:14:55.000Z" },
    { "id": "cm1x8k2p40002", "externalId": "L3-LOBBY-02", "name": "Lobby right", "online": false, "lastSeenAt": "2026-10-02T07:41:09.000Z" }
  ]
}`,
          },
        ],
      },
      {
        title: "Locations — GET /v1/locations and /v1/locations/{id}",
        blocks: [
          {
            type: "p",
            text: "Locations are the rooms and places used by event boards and room displays. Each has `name`, `label` (building or area), `floor`, `timezone`, `active` and `screenCount`. Filter the list with `active=true`. The single-location response adds the screens linked to it.",
          },
        ],
      },
      {
        title: "Give groups and locations your own IDs",
        blocks: [
          {
            type: "ul",
            items: [
              "Screen group: open it on the Screen Groups page, click Group settings, and fill in External ID.",
              "Location: open it from Locations, fill in External ID, and click Save.",
              "The rules are the same as for screens: letters, digits and `. _ : / -`, up to 100 characters, unique within your organization.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Screens API", to: "/developers/screens-api" },
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
    ],
    keywords: ["spaces", "workspaces", "groups", "screen groups", "locations", "rooms", "zones", "external id", "ext:"],
  },

  {
    slug: "reports-api",
    category: "developers",
    route: "/developers/reports-api",
    group: "Reference",
    title: "Reports API: proof of play & uptime",
    summary: "Pull what played where and for how long, and how reliably each screen ran, into your own reporting.",
    intro:
      "The same figures as the Proof of play and Proof of Operation reports in the app, as JSON, for billing advertisers, service-level reporting or your own dashboards.",
    prerequisites: ["A key with the `reports:read` scope."],
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Proof of play — GET /v1/reports/proof-of-play",
        blocks: [
          {
            type: "table",
            head: ["Query parameter", "Description"],
            rows: [
              ["`from`, `to`", "The window, ISO 8601. Default: the last 7 days. At most 92 days."],
              ["`screenId`", "Only this screen."],
              ["`groupId`", "Only the screens in this screen group."],
            ],
          },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl "${BASE}/reports/proof-of-play?from=2026-09-01T00:00:00Z&to=2026-10-01T00:00:00Z" \\
  -H "X-API-Key: pp_live_your_key"`,
          },
          {
            type: "code",
            title: "Response (shortened)",
            lang: "json",
            code: `{
  "from": "2026-09-01T00:00:00.000Z",
  "to": "2026-10-01T00:00:00.000Z",
  "totalMs": 2419200000,
  "screens": 4,
  "rows": [
    {
      "screenId": "cm1x8k2p40001",
      "screenName": "Lobby left",
      "contentType": "PLAYLIST",
      "contentId": "cm1pl2c3d0007",
      "contentName": "Lobby loop",
      "totalMs": 604800000,
      "sessions": 31,
      "firstAt": "2026-09-01T00:00:00.000Z",
      "lastAt": "2026-09-30T23:59:58.000Z"
    }
  ]
}`,
          },
          {
            type: "note",
            tone: "info",
            text: "Rows for channels also carry `items`: how much airtime each item in the channel's rotation got and how many times it played.",
          },
        ],
      },
      {
        title: "Uptime of a screen — GET /v1/screens/{id}/uptime",
        blocks: [
          {
            type: "p",
            text: "Takes `from` and `to` (default: the last 24 hours, at most 60 days). Returns `uptimePct`, the longest unbroken run, `restarts` (unexpected and scheduled), `autoRecoveries`, `connectivityInterruptions`, a plain-language `summary`, the `issues` found, and a `timeline` you can draw. `status.code` is one of `online_playing`, `online_idle`, `operating_partial`, `offline`, `issue` or `insufficient` (not enough telemetry to judge).",
          },
          {
            type: "code",
            title: "Response (shortened)",
            lang: "json",
            code: `{
  "screenId": "cm1x8k2p40001",
  "status": { "code": "online_playing", "label": "Online & Playing", "tone": "good" },
  "uptimePct": 99.6,
  "longestRun": "6 days 4 h",
  "restarts": { "unexpected": 0, "scheduled": 7, "note": "…" },
  "autoRecoveries": 1,
  "connectivityInterruptions": 3,
  "summary": "…"
}`,
          },
          {
            type: "note",
            tone: "tip",
            text: "Uptime is worked out from what the player reports. A screen that is switched off, or whose player can't reach PulsePanels, reports nothing, so it counts as down for that time.",
          },
        ],
      },
    ],
    related: [
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
      { label: "Proof of play in the app", to: "/account/proof-of-play" },
    ],
    keywords: ["proof of play", "airtime", "report", "reports", "uptime", "sla", "availability", "proof of operation", "advertising"],
  },

  {
    slug: "emergency-api",
    category: "developers",
    route: "/developers/emergency-api",
    group: "Reference",
    title: "Emergency API: send, stop & delivery",
    summary: "Send an emergency alert from a fire panel or building system, stop it, and confirm each screen displayed it.",
    intro:
      "When an emergency alert is sent from the Emergency Center, every targeted screen reports back whether it received and displayed it. These endpoints let a building-management or safety system read that state.",
    prerequisites: ["A key with `emergency:read` to read alerts, and `emergency:trigger` to send and stop them."],
    readingTime: 6,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "List alerts — GET /v1/emergency/alerts",
        blocks: [
          {
            type: "p",
            text: "Newest first. Filter with `status` (any of `draft`, `scheduled`, `active`, `expired`, `stopped`, `failed`, comma-separated) and cap with `limit` (default 50, max 100). `live` is `true` while the alert is on screens.",
          },
          { type: "code", title: "Alerts on screens right now", lang: "bash", code: `curl "${BASE}/emergency/alerts?status=active" -H "X-API-Key: pp_live_your_key"` },
        ],
      },
      {
        title: "Check delivery — GET /v1/emergency/alerts/{id}",
        blocks: [
          { type: "p", text: "Adds a `summary` and a `delivery` entry for every targeted screen:" },
          {
            type: "table",
            head: ["Delivery status", "Meaning"],
            rows: [
              ["`DISPLAYED`", "The screen is showing the alert."],
              ["`RECEIVED`", "The screen has the alert but hasn't confirmed it's showing yet."],
              ["`FAILED`", "The screen reported a problem; `message` says what."],
              ["`PENDING`", "The screen is online but hasn't answered yet."],
              ["`OFFLINE`", "The screen is offline, so it can't show the alert."],
            ],
          },
        ],
      },
      {
        title: "Templates — GET /v1/emergency/templates",
        blocks: [{ type: "p", text: "Your saved alert templates: `key`, `name`, `priority`, colour, sound and language." }],
      },
      {
        title: "Send an alert — POST /v1/emergency/alerts",
        blocks: [
          {
            type: "p",
            text: "Pick a `template` and a `target`. The alert takes over the targeted screens until it's stopped, or until `durationSec` runs out if you set one.",
          },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`template`", "`fire`, `evacuation`, `security`, `medical`, `weather`, `maintenance`, `custom` (default), or the key of a template you saved in the Emergency Center. Your saved changes to a built-in template are used."],
              ["`target`", "`{ \"all\": true }`, or any of `screens`, `groups`, `locations`: lists of ids or `ext:<externalId>`. Groups and locations are turned into their screens when the alert is sent."],
              ["`title`, `instructions`", "Override the template's heading and its lines (up to 10)."],
              ["`message`", "A line of text: its own line on the medical and weather designs, the first instruction elsewhere."],
              ["`ar`", "Arabic `title`, `message`, `instructions` for bilingual alerts. If you change the English and send no Arabic, the alert shows English only."],
              ["`priority`, `sound`, `lang`", "Default to the template's."],
              ["`durationSec`", "Stop automatically after this many seconds (10 to 604800)."],
              ["`startsAt`", "Schedule it for later instead of now."],
            ],
          },
          { type: "code", title: "Evacuate one floor", lang: "bash", code: `curl -X POST ${BASE}/emergency/alerts \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: alarm-2026-10-02-0915" \\
  -d '{"template": "evacuation", "message": "Use stair B", "target": {"groups": ["ext:L3-EAST"]}}'` },
          { type: "code", title: "Response", lang: "json", code: `{ "alertId": "cm1em3rg00042", "status": "ACTIVE", "scheduled": false, "targetedScreens": 8 }` },
          {
            type: "note",
            tone: "warning",
            text: "The Emergency Center's send PIN does not apply to the API: giving a key `emergency:trigger` is the authorisation. Give it only to the system that should raise alerts, limit the key to that system's IP address, and always send an `Idempotency-Key` so a retry can't raise a second alert.",
          },
        ],
      },
      {
        title: "Stop alerts",
        blocks: [
          {
            type: "ul",
            items: [
              "`POST /v1/emergency/alerts/{id}/stop` stops one alert. Screens go back to their content within seconds.",
              "`POST /v1/emergency/stop-all` stops every active and scheduled alert, for example when the fire panel is reset.",
              "A key limited to one Space only reaches its own Space: `target.all` means all screens in that Space, and it can only stop alerts it sent.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
      { label: "Emergency Center", to: "/modules/emergency-center" },
    ],
    keywords: ["emergency", "alert", "evacuation", "fire alarm", "fire panel", "delivery", "displayed", "safety", "send alert", "stop alert"],
  },

{
    slug: "control-api",
    category: "developers",
    route: "/developers/control-api",
    group: "Reference",
    title: "Control & content API",
    summary: "Send commands to screens, set volume and brightness, take a fresh screenshot, and change what screens and groups play.",
    intro:
      "These endpoints change what's on your screens, so they need write scopes that are never given by default: `screens:control` for commands, volume, brightness and screenshots, and `content:publish` for content. Every change is recorded in the audit log under the key's name.",
    prerequisites: ["A key with `screens:control` and/or `content:publish`. Limit it to your system's IP address."],
    readingTime: 5,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Send a command — POST /v1/screens/{id}/commands",
        blocks: [
          {
            type: "table",
            head: ["command", "What the screen does"],
            rows: [
              ["`reload`", "Reloads its content."],
              ["`restart`", "Restarts the player app."],
              ["`reboot`", "Restarts the device (where the player is allowed to)."],
              ["`clear_cache`", "Clears downloaded content, then downloads it again."],
            ],
          },
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST ${BASE}/screens/ext:L3-LOBBY-01/commands \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"command": "reload"}'` },
          {
            type: "p",
            text: "The answer is `202` with `status: \"queued\"`. An online screen picks the command up within seconds; an offline one when it reconnects. `GET /v1/screens/{id}/commands` shows a command still waiting. To send one command to up to 200 screens, use `POST /v1/commands` with `{ \"command\": \"reload\", \"screens\": [\"ext:L3-LOBBY-01\", …] }`; screens that couldn't be queued are listed in `failed`.",
          },
        ],
      },
      {
        title: "Volume, mute and brightness — PATCH /v1/screens/{id}",
        blocks: [
          { type: "p", text: "Send any of `volume` (0–100), `muted` (true/false) and `brightness` (0–100). The screen applies it within seconds, for example to dim screens at night or mute them during an announcement." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X PATCH ${BASE}/screens/ext:L3-LOBBY-01 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"brightness": 40, "muted": true}'` },
        ],
      },
      {
        title: "Take a fresh screenshot — POST /v1/screens/{id}/screenshot",
        blocks: [
          { type: "p", text: "Asks the screen to capture what it's showing. Wait a few seconds, then `GET /v1/screens/{id}/screenshot`: when its `X-Captured-At` is later than the `previousCapturedAt` you got back, it's the new one." },
        ],
      },
      {
        title: "Change what a screen plays — PUT /v1/screens/{id}/content",
        blocks: [
          { type: "p", text: "Send `type` (`channel`, `playlist`, `media` or `app`) and the content's `id` (from the content lists). It's assigned and published at once, like Apply changes in the app. Add `\"publish\": false` to only stage it, so someone can review and apply it in the app." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X PUT ${BASE}/screens/ext:L3-LOBBY-01/content \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"type": "playlist", "id": "cm1pl2c3d0007"}'` },
          {
            type: "note",
            tone: "info",
            text: "A screen that's in a screen group leaves the group when you give it its own content, as in the app. To change a whole group, use `PUT /v1/groups/{id}/content`, which publishes to every screen in the group.",
          },
          { type: "p", text: "If your licence is paused, content can be staged but not published (`403 license_paused`)." },
        ],
      },
    ],
    related: [
      { label: "Screens API", to: "/developers/screens-api" },
      { label: "Spaces, screen groups & locations API", to: "/developers/organization-api" },
    ],
    keywords: ["command", "reboot", "restart", "reload", "clear cache", "volume", "mute", "brightness", "screenshot", "publish", "assign content", "control"],
  },

  {
    slug: "bookings-api",
    category: "developers",
    route: "/developers/bookings-api",
    group: "Reference",
    title: "Room bookings API",
    summary: "Push bookings from your room-booking system to room displays and event boards, keyed by your own booking id.",
    intro:
      "Room displays and event boards show the events of their location. A booking system can keep them up to date through the API: it sends each booking with its own id, and sends it again whenever it changes.",
    prerequisites: [
      "A key with the `events:write` scope.",
      "Your locations' External IDs set to your booking system's room ids (optional, but it saves you mapping ids).",
    ],
    readingTime: 4,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Create or update a booking — PUT /v1/locations/{id}/events/ext:{bookingId}",
        blocks: [
          { type: "p", text: "The first PUT with a booking id creates the event; any later PUT with the same id updates it. Send the whole booking each time." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X PUT ${BASE}/locations/ext:ROOM-12/events/ext:booking-8812 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"title": "Board meeting", "startAt": "2026-10-05T09:00:00Z", "endAt": "2026-10-05T10:00:00Z", "organizerName": "Finance"}'` },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`title`, `startAt`, `endAt`", "Required. Times in ISO 8601; an event lasts at most 31 days."],
              ["`timezone`", "IANA name, e.g. `Asia/Qatar`. Default: the location's, else your organization's."],
              ["`description`, `organizerName`, `organizerEmail`", "Optional."],
              ["`status`", "`confirmed` (default), `tentative` or `cancelled`."],
              ["`isPrivate`", "`true` shows \"Private event\" instead of the title."],
            ],
          },
        ],
      },
      {
        title: "Cancel or delete a booking",
        blocks: [
          {
            type: "ul",
            items: [
              "To show it as cancelled, PUT it again with `\"status\": \"cancelled\"`.",
              "To remove it, `DELETE /v1/locations/{id}/events/ext:{bookingId}`.",
              "Only events created through the API can be changed or deleted this way. Events typed in the app or synced from Google or Microsoft calendars are left alone (`409 not_api_event`).",
            ],
          },
        ],
      },
      {
        title: "Read a location's events — GET /v1/locations/{id}/events",
        blocks: [
          { type: "p", text: "Events from every source overlapping a window: `from` and `to`, default now to 7 days ahead. Add `includeCancelled=true` to include cancelled ones. This needs only `screens:read`." },
          {
            type: "note",
            tone: "info",
            text: "Your booking system is the source of truth: two bookings for the same room and time are both accepted. Events are removed automatically once they have ended.",
          },
        ],
      },
    ],
    related: [
      { label: "Spaces, screen groups & locations API", to: "/developers/organization-api" },
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
    ],
    keywords: ["booking", "bookings", "room booking", "meeting room", "events", "calendar", "room display", "event board", "reservation"],
  },

  // ── Clinics (hospital information systems) ─────────────────────────────────
  {
    slug: "clinics-api",
    category: "developers",
    route: "/developers/clinics-api",
    group: "Reference",
    title: "Clinics API for hospital systems",
    summary: "Drive clinic door screens from a hospital information system: clinics, doctor sessions, visits and patient calls, keyed by your own ids.",
    intro:
      "A hospital information system (HIS) keeps each clinic's door screen up to date: it sends the clinic rooms once, the doctors' sessions from the roster, and each visit as it is booked, arrives, is called and finishes. PulsePanels works out what each door shows (doctor, status, ticket being served, next patients, waiting count, delay) and pushes it to the screens within a second. Everything is addressed by your HIS's own ids, so you never store ours.",
    prerequisites: [
      "The Clinic Flow module and the API & integrations add-on on the organisation.",
      "A key with the `clinics:write` scope (and `clinics:read` to read back). Limit it to your HIS servers' IP addresses.",
    ],
    readingTime: 8,
    status: "published",
    lastUpdated: "2026-10-07",
    steps: [
      {
        title: "Patient privacy: what you may send",
        blocks: [
          {
            type: "p",
            text: "A visit is a ticket number, an appointment time, a status and the time of each change. Nothing else is read. Every body is checked against a fixed list of fields; any other field (a patient name, record number, ID or diagnosis) is dropped before anything is saved, and its name is returned in `ignoredFields` so you can see it was not kept.",
          },
          {
            type: "note",
            tone: "warning",
            text: "The ticket number is shown on a public screen. Send a queue or booking number such as `P-024`, never a name: it may use letters, digits, spaces and `. _ # / -`, up to 20 characters. Visits are deleted automatically after their clinic day.",
          },
        ],
      },
      {
        title: "Map your HIS events to calls",
        blocks: [
          {
            type: "table",
            head: ["When this happens in the HIS", "Call"],
            rows: [
              ["A clinic room is set up (once)", "`PUT /v1/clinics/ext:{roomId}`"],
              ["The doctor roster for a clinic is published or changed", "`PUT /v1/clinics/ext:{roomId}/sessions/ext:{sessionId}`"],
              ["An appointment is booked or rescheduled", "`PUT …/visits/ext:{appointmentId}` with `ticketNumber`, `scheduledAt`, `status: booked`"],
              ["The patient registers on arrival", "`PUT …/visits/ext:{appointmentId}` with `status: arrived`"],
              ["The doctor calls the patient (or calls again)", "`POST …/visits/ext:{appointmentId}/call`"],
              ["The consultation starts", "`PUT …` with `status: in_consultation`"],
              ["The consultation ends, or the patient doesn't come", "`PUT …` with `status: completed` or `no_show`"],
              ["The appointment is cancelled", "`PUT …` with `status: cancelled`, or `DELETE …/visits/ext:{appointmentId}`"],
              ["A procedure starts, the doctor takes a break, the clinic closes early", "`PUT /v1/clinics/ext:{roomId}/status`"],
            ],
          },
          {
            type: "p",
            text: "Every PUT is an upsert keyed by your id, so sending the same update twice is harmless. A second `/call` is different: it is a recall and is announced again, so send an `Idempotency-Key` header when you retry a call after a network error.",
          },
        ],
      },
      {
        title: "Create or update a clinic — PUT /v1/clinics/{id}",
        blocks: [
          { type: "p", text: "`PUT /v1/clinics/ext:{roomId}` creates the clinic the first time and updates it after that. Addressed by our id, it only updates." },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl -X PUT ${BASE}/clinics/ext:OPD-12 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"name": "Sports Medicine Clinic 12", "nameAr": "عيادة الطب الرياضي ١٢", "roomLabel": "12", "timezone": "Asia/Qatar"}'`,
          },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`name`", "Required when creating. Up to 120 characters."],
              ["`nameAr`", "Optional Arabic name."],
              ["`roomLabel`", "Optional, up to 20 characters. Shown large on the door, e.g. `12`."],
              ["`timezone`", "IANA name, e.g. `Asia/Qatar`. Decides the clinic day. Default: the organisation's."],
              ["`retentionDays`", "Optional, 0 to 90: keep visits this many days after their clinic day (default 0)."],
            ],
          },
        ],
      },
      {
        title: "Send the doctor's sessions — PUT /v1/clinics/{id}/sessions/ext:{sessionId}",
        blocks: [
          { type: "p", text: "A session is a block of a doctor's roster in this clinic. A gap between two sessions on the same day shows as a break; outside any session the door shows Closed with the next session." },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl -X PUT ${BASE}/clinics/ext:OPD-12/sessions/ext:ROSTER-7781 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"doctorName": "Dr. Ahmed Hassan", "doctorNameAr": "د. أحمد حسن", "specialty": "Sports Medicine", "specialtyAr": "الطب الرياضي", "doctorId": "DOC-77", "startsAt": "2026-10-07T08:00:00+03:00", "endsAt": "2026-10-07T13:00:00+03:00"}'`,
          },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`doctorName`, `startsAt`, `endsAt`", "Required. A session lasts at most 24 hours."],
              ["`doctorNameAr`, `specialty`, `specialtyAr`, `doctorId`", "Optional."],
              ["`photoUrl`", "Optional `https://` address of the doctor's photo; otherwise the door shows initials."],
              ["`status`", "`scheduled` (default), `on_break` (optionally with `breakUntil`) or `cancelled`."],
            ],
          },
          { type: "p", text: "`DELETE …/sessions/ext:{sessionId}` removes one. `GET /v1/clinics/{id}/sessions` lists them (default: from 18 hours ago to 7 days ahead)." },
        ],
      },
      {
        title: "Send each visit — PUT /v1/clinics/{id}/visits/ext:{appointmentId}",
        blocks: [
          { type: "p", text: "Send the visit when it is booked, and again on every change. Moving to a status stamps its time with `at` (default: now): arrived stamps the arrival, called the call, in_consultation the start and completed the end." },
          {
            type: "code",
            title: "Booked",
            lang: "bash",
            code: `curl -X PUT ${BASE}/clinics/ext:OPD-12/visits/ext:APT-1001 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"ticketNumber": "P-024", "scheduledAt": "2026-10-07T10:20:00+03:00", "status": "booked"}'`,
          },
          {
            type: "code",
            title: "Arrived (a field we don't store is reported back)",
            lang: "bash",
            code: `curl -X PUT ${BASE}/clinics/ext:OPD-12/visits/ext:APT-1001 \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"status": "arrived", "patientName": "…"}'`,
          },
          {
            type: "code",
            title: "Response",
            lang: "json",
            code: `{
  "created": false,
  "visit": {
    "id": "cmux5ue5n0007", "externalId": "APT-1001", "ticketNumber": "P-024",
    "serviceDate": "2026-10-07", "scheduledAt": "2026-10-07T07:20:00.000Z",
    "status": "arrived", "arrivedAt": "2026-10-07T07:12:40.000Z",
    "calledAt": null, "startedAt": null, "completedAt": null, "callCount": 0
  },
  "ignoredFields": ["patientName"]
}`,
          },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`ticketNumber`", "Required when the visit is created. Shown on the screen."],
              ["`scheduledAt`", "Appointment time (ISO 8601). Orders the next patients and drives the delay notice."],
              ["`status`", "`booked`, `arrived`, `called`, `in_consultation`, `completed`, `no_show` or `cancelled`."],
              ["`at`", "When the change happened, if not now."],
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Visits are always addressed by your appointment id (`ext:`). The clinic day is the appointment's local date in the clinic's time zone; a session that runs past midnight keeps its waiting patients on the door.",
          },
        ],
      },
      {
        title: "Call a patient — POST /v1/clinics/{id}/visits/ext:{appointmentId}/call",
        blocks: [
          { type: "p", text: "The door highlights the ticket with Please enter and plays its tone and announcement. Call the same visit again to recall it: it is announced again and `callCount` goes up." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST ${BASE}/clinics/ext:OPD-12/visits/ext:APT-1001/call -H "X-API-Key: pp_live_your_key"` },
        ],
      },
      {
        title: "Override the door — PUT /v1/clinics/{id}/status",
        blocks: [
          { type: "p", text: "Show `procedure` (please do not enter), `on_break` or `closed`, optionally until a time and with a short note in English and Arabic. Send `{\"status\": null}` to go back to what the sessions and visits give." },
          {
            type: "code",
            title: "Request",
            lang: "bash",
            code: `curl -X PUT ${BASE}/clinics/ext:OPD-12/status \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"status": "procedure", "note": "Isolation precautions", "noteAr": "احتياطات العزل", "until": "2026-10-07T10:30:00+03:00"}'`,
          },
        ],
      },
      {
        title: "Read what the door shows — GET /v1/clinics/{id}",
        blocks: [
          { type: "p", text: "Returns the clinic and its `state`: exactly what its door shows now. `GET /v1/clinics` lists clinics (paginated) and `GET /v1/clinics/{id}/visits` lists a clinic day's visits (today by default, `?date=YYYY-MM-DD` while still kept). These need `clinics:read`." },
          {
            type: "code",
            title: "state (shortened)",
            lang: "json",
            code: `{
  "status": "IN_CONSULTATION",
  "doctor": { "name": "Dr. Ahmed Hassan", "nameAr": "د. أحمد حسن", "specialty": "Sports Medicine" },
  "session": { "startsAt": "2026-10-07T05:00:00.000Z", "endsAt": "2026-10-07T10:00:00.000Z" },
  "nowServing": { "ticket": "P-024", "since": "2026-10-07T07:21:10.000Z" },
  "lastCall": { "ticket": "P-024", "at": "2026-10-07T07:21:02.000Z", "count": 1 },
  "next": [ { "ticket": "P-025", "scheduledAt": "2026-10-07T07:35:00.000Z", "arrived": true } ],
  "waitingCount": 3,
  "avgWaitMinutes": 12,
  "delayMinutes": 15,
  "resumeAt": null,
  "changesAt": "2026-10-07T10:00:00.000Z"
}`,
          },
        ],
      },
      {
        title: "Errors you may meet",
        blocks: [
          {
            type: "table",
            head: ["Status and code", "Meaning"],
            rows: [
              ["`400 invalid_body`", "A field is missing or not valid, e.g. a ticket number with a name in it. The message names the field."],
              ["`400 invalid_visit_ref`", "Address visits by your id: `visits/ext:{appointmentId}`."],
              ["`403 clinic_flow_not_enabled`", "The organisation doesn't have the Clinic Flow module."],
              ["`403 space_not_allowed`", "A key limited to one Space tried to change an organisation-wide clinic."],
              ["`404`", "No clinic, session or visit with that id for this key."],
              ["`409 external_id_taken`", "Another clinic already uses that id."],
              ["`409 visit_limit`", "A clinic can have up to 1,000 visits per day."],
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "A key limited to one Space creates its clinics in that Space; only that Space's screens can show them.",
          },
        ],
      },
    ],
    related: [
      { label: "Clinic Flow: clinic door screens", to: "/modules/clinic-flow" },
      { label: "Authentication, keys & scopes", to: "/developers/authentication" },
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
    ],
    keywords: ["clinic", "clinics", "hospital", "his", "hl7", "fhir", "appointment", "visit", "patient", "doctor", "roster", "outpatient", "medicloud", "queue", "call patient"],
  },

  // ── Live data ──────────────────────────────────────────────────────────────
  {
    slug: "data-feeds-api",
    category: "developers",
    route: "/developers/data-feeds-api",
    group: "Reference",
    title: "Live data feeds API",
    summary: "Send live values (temperatures, CO₂, free parking spaces, statuses) and show them on screens within a second or two.",
    intro:
      "A data feed is a named value your system keeps up to date: `floor3.temp`, `carpark.free`, `lobby.status`. Send a value whenever it changes. Screens showing it update within a second or two through the Live Data app, or through `{{key}}` placeholders in Canvas text and News Scroller items.",
    prerequisites: ["A key with the `data:write` scope (and `data:read` to read values back)."],
    readingTime: 5,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Send a value — PUT /v1/data/{key}",
        blocks: [
          { type: "p", text: "The first PUT creates the key. Later PUTs replace its value. Keys are 1 to 100 characters of letters, digits and `.` `_` `-`; dots make good groupings, for example `floor3.temp`." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X PUT ${BASE}/data/floor3.temp \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"value": 22.5, "unit": "°C", "label": "Floor 3"}'` },
          {
            type: "table",
            head: ["Field", "Notes"],
            rows: [
              ["`value`", "Required. A number, text (up to 500 characters), `true`/`false` (shown as On/Off) or `null` (shown as a dash)."],
              ["`unit`", "Optional, up to 20 characters, e.g. `°C`, `ppm`, `%`. Shown after numbers."],
              ["`label`", "Optional, up to 80 characters. The Live Data app shows it when the tile has no label of its own."],
            ],
          },
        ],
      },
      {
        title: "Send many values at once — POST /v1/data",
        blocks: [
          { type: "p", text: "Up to 200 keys per request. Each value is a bare value, or an object with `value`, `unit` and `label`. A BMS that reads all its points every 30 seconds can send them in one call." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST ${BASE}/data \\
  -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"values": {"floor3.temp": 22.5, "floor3.co2": {"value": 840, "unit": "ppm"}, "carpark.free": 41, "lobby.status": "Open"}}'` },
          { type: "code", title: "Response", lang: "json", code: `{ "updated": 3, "created": 1, "points": [ { "key": "floor3.temp", "value": 22.5, "unit": null, "label": null, "spaceId": null, "updatedAt": "2026-10-03T09:30:00.000Z" } ] }` },
        ],
      },
      {
        title: "Show the values on screens",
        blocks: [
          {
            type: "ul",
            items: [
              "Live Data app: in Apps, open the App Store and add Live Data (under Live Feeds). Pick the keys, give them labels and units, and set amber and red thresholds, for example CO₂ amber at 1000 and red at 1400. Then put the app on a screen, a channel zone or a playlist.",
              "Canvas text: type `{{floor3.temp}}` in a text element, for example `Floor 3 is {{floor3.temp|1}}°C`. Add `|N` to round a number to N decimal places.",
              "News Scroller: placeholders work in items too, for example `Car park: {{carpark.free}} spaces free`.",
              "A key with no value yet shows a dash. In the Live Data app, a tile dims when its value hasn't been updated for a while (you choose how long), so a stopped sensor is visible.",
            ],
          },
        ],
      },
      {
        title: "Act on values with rules",
        blocks: [
          { type: "p", text: "A rule fires a trigger while a value is past a threshold, and stops it when the value is back. For example: when `floor3.co2` is at or above 1000, fire a \"Ventilate\" trigger that shows a notice on that floor's screens; stop it once CO₂ is back under 900." },
          {
            type: "ol",
            items: [
              "Set up the trigger first (Developer → Triggers).",
              "Under Developer → Live data → Rules, click New rule: pick the key, the condition (at or above, at or below, equals, is not), the value, and the trigger.",
              "For at or above / at or below, set a clear level a little past the threshold so a reading that hovers at the limit doesn't switch the trigger on and off.",
            ],
          },
          { type: "note", tone: "info", text: "Rules work for every data source: the API, a connector, or a BMS sending values. Only changes act: the trigger fires once when the condition starts and stops once when it ends. If the value already meets a new rule, its trigger fires straight away." },
        ],
      },
      {
        title: "Read values back and tidy up",
        blocks: [
          {
            type: "ul",
            items: [
              "`GET /v1/data` lists every key the API key can see (paginated, filter with `?prefix=floor3.`). `GET /v1/data/{key}` returns one. Both need `data:read`.",
              "`DELETE /v1/data/{key}` removes a key you no longer send. You can also delete keys under Developer → Live data.",
              "An organization can have up to 2,000 keys. Each value is stored as its latest value only; there is no history.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Keys sent by a Space-restricted API key belong to that Space: only that Space's screens see them, and only keys of that Space (or organization-wide keys) can change them. Keys sent by an organization-wide key are visible to every screen.",
          },
        ],
      },
    ],
    related: [
      { label: "Triggers API", to: "/developers/triggers-api" },
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
      { label: "Pagination, errors & rate limits", to: "/developers/conventions" },
    ],
    keywords: ["data feed", "live data", "sensor", "temperature", "co2", "parking", "kpi", "bms points", "placeholder", "variables", "dashboard", "telemetry"],
  },

  // ── Triggers ───────────────────────────────────────────────────────────────
  {
    slug: "triggers-api",
    category: "developers",
    route: "/developers/triggers-api",
    group: "Reference",
    title: "Triggers API",
    summary: "Set up named actions on the Developer page (an emergency alert, or content shown for a while) and fire them by key or through a secret URL.",
    intro:
      "A trigger is a saved action with a short key, like `fire-alarm` or `vip-arrival`. You set up what it does once on the Developer page; your system only has to say “fire” and later “stop”. Systems that can only call a URL (Niagara, Crestron, Home Assistant, a fire panel's IP module) use the trigger's secret inbound URL instead of an API key.",
    prerequisites: ["An Owner or Admin to set up triggers on the Developer page.", "A key with the `triggers:fire` scope, or a trigger's inbound URL."],
    readingTime: 5,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Set up a trigger",
        blocks: [
          {
            type: "ol",
            items: [
              "Go to Account Settings → Developer → Triggers and click New trigger.",
              "Give it a name. The key is filled in from the name; change it if your system uses a different one.",
              "Choose what happens when it fires: Show an emergency alert (a template, an optional title and message, and how long it stays on screen), or Show content for a while (a channel, playlist, image, video or app, for 1 minute up to 24 hours).",
              "Choose where: every screen, screen groups, locations or individual screens. Limit the trigger to a Space to keep it inside that Space.",
              "Save, then use Fire on the Developer page to test it on a test screen.",
            ],
          },
        ],
      },
      {
        title: "Fire and stop with an API key",
        blocks: [
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST ${BASE}/triggers/fire-alarm/fire -H "X-API-Key: pp_live_your_key"
curl -X POST ${BASE}/triggers/fire-alarm/stop -H "X-API-Key: pp_live_your_key"` },
          { type: "code", title: "Response", lang: "json", code: `{ "trigger": "fire-alarm", "action": "emergency", "status": "fired", "alertId": "cm1x…", "alertStatus": "ACTIVE", "targetedScreens": 48 }` },
          {
            type: "table",
            head: ["status", "Meaning"],
            rows: [
              ["`fired`", "The action ran. Content triggers also return `screens`, `failed` and `endsAt`."],
              ["`already_active`", "An emergency trigger whose alert is still on screen: nothing new is sent, so repeating \"alarm on\" is safe."],
              ["`stopped`", "Stop ended the alert, or put the screens back on their own content."],
              ["`nothing_active`", "Stop found nothing still running."],
            ],
          },
          { type: "p", text: "`GET /v1/triggers` lists the triggers the key can fire and whether each is on screen. A Space-restricted key sees only its Space's triggers." },
        ],
      },
      {
        title: "Fire from a system that can only call a URL",
        blocks: [
          { type: "p", text: "Each trigger has an inbound URL. It is shown once, when you create the trigger: copy it into the calling system straight away. PulsePanels keeps only a fingerprint of it, so it can’t show the URL again. If it is lost, click New URL on the trigger (the old URL stops working). POST to the URL to fire, and POST to the same URL with `/stop` added to stop. No API key or headers are needed: the secret part of the URL is the credential." },
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST https://app.pulsepanels.com/api/hooks/triggers/trg_your_secret
curl -X POST https://app.pulsepanels.com/api/hooks/triggers/trg_your_secret/stop` },
          {
            type: "ul",
            items: [
              "Only POST works. A GET (a browser, a link preview in a chat app) never fires a trigger.",
              "Each URL accepts up to 30 calls a minute. Repeated calls with wrong URLs from one address are blocked for a minute.",
              "Treat the URL like a password. If it leaks, click New URL: the old one stops working immediately.",
            ],
          },
        ],
      },
      {
        title: "How content triggers behave",
        blocks: [
          {
            type: "ul",
            items: [
              "The screens show the trigger's content for the set time, then go back to their own content by themselves, even if nobody calls stop. Their own content and schedules are never changed.",
              "While it's showing, it takes priority over the screen group's schedule.",
              "Firing again restarts the timer. Stop ends it early on the screens it started on.",
              "A screen that can't take it (for example a video wall panel) is listed in `failed`; the others still show it.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Emergency triggers fire straight away on real screens; the Emergency Center's send PIN doesn't apply. Give `triggers:fire` only to systems you trust, and test with a trigger that targets one screen.",
          },
        ],
      },
    ],
    related: [
      { label: "Emergency API: alerts & delivery", to: "/developers/emergency-api" },
      { label: "Live data feeds API", to: "/developers/data-feeds-api" },
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
    ],
    keywords: ["trigger", "triggers", "webhook in", "inbound url", "fire alarm", "evacuation", "crestron", "niagara", "home assistant", "automation", "vip", "takeover"],
  },

  // ── Webhooks ───────────────────────────────────────────────────────────────
  {
    slug: "webhooks",
    category: "developers",
    route: "/developers/webhooks",
    group: "Webhooks",
    title: "Webhooks: events, signatures & retries",
    summary: "Receive screen, content, alert, trigger and account events as they happen, check they really came from PulsePanels, and know what happens when your endpoint is down.",
    intro:
      "A webhook is a URL on your system that PulsePanels calls with a signed POST when something happens: a screen goes offline, an alert is sent and shown on screens, a trigger fires, content is published. It saves you polling, and your system hears about changes within seconds (offline screens within a minute or so of the threshold passing).",
    prerequisites: [
      "An HTTPS endpoint reachable from the internet (on-premises servers can also post to your local network).",
      "The Owner or an Admin adds it under Account Settings → Developer → Add endpoint.",
    ],
    readingTime: 7,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Events",
        blocks: [
          {
            type: "table",
            head: ["Event", "Sent when"],
            rows: [
              ["`screen.offline`", "A screen that was online has been silent for longer than Consider a screen offline after in Screen settings (10 minutes unless changed). Sent once per outage."],
              ["`screen.online`", "A screen that was reported offline is heard from again."],
              ["`screen.paired`", "A new screen is paired, by code or QR. `pairedBy`: `code` or `qr`."],
              ["`screen.unpaired` / `screen.deleted`", "A screen is unpaired from its device, or deleted."],
              ["`screen.content_published`", "A screen's content is published. `source`: `screen` (its own publish, in the app or through the API), `group` (its screen group was applied, or it joined one), `schedule` (the group's schedule switched slot). Includes `content: { type, id }`."],
              ["`command.delivered`", "A screen picked up a command (reload, restart, reboot, clear cache). Screens don't report completion: a reboot then shows as `screen.offline` and `screen.online`."],
              ["`emergency.sent`", "An alert is sent or scheduled, from the Emergency Center, the API or a trigger. `status`: `active` or `scheduled`; `endsAt` is when it ends by itself (null = until stopped); `sentBy` says who."],
              ["`emergency.stopped`", "An alert is stopped. `stoppedBy` says who."],
              ["`emergency.delivery`", "A screen reports it displayed an alert (`status: displayed`) or couldn't (`failed`). One event per screen, so it isn't ticked by default."],
              ["`trigger.fired` / `trigger.stopped`", "A trigger ran or was stopped. Same fields as the fire/stop response, plus `firedVia` / `stoppedVia`: `api_key`, `inbound_url`, `user`, `connector` or `rule`."],
              ["`licence.state_changed`", "Your licence became `active`, `paused` or `suspended`."],
              ["`ping`", "You click Test on the Developer page."],
            ],
          },
          {
            type: "p",
            text: "Choose the events per endpoint under Developer → Webhooks → Events. A new endpoint gets every event except `emergency.delivery`. `sentBy`, `stoppedBy` and `publishedBy` are `{ type, id }` with type `user`, `api_key`, `trigger` or `system`.",
          },
          {
            type: "code",
            title: "Body of a screen.offline delivery",
            lang: "json",
            code: `{
  "id": "evt_9b2c0f6e1a7d4c3b8e5f0a12",
  "event": "screen.offline",
  "createdAt": "2026-10-02T08:15:00.000Z",
  "data": {
    "screenId": "cm1x8k2p40001",
    "name": "Lobby left",
    "lastSeenAt": "2026-10-02T08:04:12.000Z"
  }
}`,
          },
          {
            type: "code",
            title: "Body of an emergency.sent delivery",
            lang: "json",
            code: `{
  "id": "evt_4f1d2a9c7b6e5d4c3b2a1f0e",
  "event": "emergency.sent",
  "createdAt": "2026-10-02T09:41:07.000Z",
  "data": {
    "alertId": "cm2a7q0f10003",
    "title": "FIRE EMERGENCY",
    "priority": "critical",
    "status": "active",
    "startsAt": "2026-10-02T09:41:07.000Z",
    "endsAt": null,
    "targetedScreens": 48,
    "spaceId": null,
    "sentBy": { "type": "trigger", "id": "cm2a6z1k80001" }
  }
}`,
          },
          {
            type: "p",
            text: "Look a screen up with `GET /v1/screens/{screenId}` to get its External ID and other details. The full schema of every event is in the OpenAPI spec under `webhooks`.",
          },
        ],
      },
      {
        title: "Headers",
        blocks: [
          {
            type: "table",
            head: ["Header", "Value"],
            rows: [
              ["`webhook-id`", "The delivery's ID. Stays the same if the delivery is retried, so use it to ignore duplicates."],
              ["`webhook-timestamp`", "When this attempt was signed, in Unix seconds."],
              ["`webhook-signature`", "`v1,` followed by the signature. During a secret rotation there are two, separated by a space."],
              ["`X-PulsePanels-Event`", "The event name."],
              ["`X-PulsePanels-Attempt`", "1 for the first attempt, 2 for the first retry, and so on."],
              ["`X-PulsePanels-Signature`", "Older signature format (`sha256=` + hex HMAC of the body, keyed with the whole secret). Still sent for existing receivers; use `webhook-signature` for new ones."],
            ],
          },
        ],
      },
      {
        title: "Verify the signature",
        blocks: [
          {
            type: "p",
            text: "Signatures follow the Standard Webhooks specification, so its ready-made libraries for most languages work as-is. To check by hand: HMAC-SHA256 of `<webhook-id>.<webhook-timestamp>.<raw body>`, keyed with your secret minus its `whsec_` prefix, base64-decoded. Compare the base64 result with each `v1,` value, and reject timestamps more than 5 minutes old.",
          },
          { type: "code", title: "Node.js", lang: "javascript", code: VERIFY_NODE },
          { type: "code", title: "Python", lang: "python", code: VERIFY_PY },
          { type: "code", title: "C#", lang: "csharp", code: VERIFY_CS },
          {
            type: "note",
            tone: "warning",
            text: "Verify against the raw body exactly as received. Parsing the JSON and re-serializing it changes spacing and breaks the signature.",
          },
        ],
      },
      {
        title: "Answer quickly, then do the work",
        blocks: [
          {
            type: "ul",
            items: [
              "Reply with any 2xx status within 8 seconds. Anything else, or no answer, counts as a failure.",
              "Do slow work (sending emails, updating a BMS) after replying, for example from a queue.",
              "Ignore a `webhook-id` you have already processed: a retry can arrive after a reply that got lost.",
              "Events can arrive out of order. Compare `createdAt` if order matters.",
            ],
          },
        ],
      },
      {
        title: "Retries, the delivery log and automatic switch-off",
        blocks: [
          {
            type: "table",
            head: ["Attempt", "When"],
            rows: [
              ["1", "Immediately"],
              ["2", "1 minute later"],
              ["3", "5 minutes after that"],
              ["4", "30 minutes after that"],
              ["5", "2 hours after that"],
              ["6", "6 hours after that"],
              ["7 (last)", "12 hours after that, about 20 hours after the event"],
            ],
          },
          {
            type: "p",
            text: "Deliveries on the Developer page lists the last 30 days of deliveries for an endpoint: status, your endpoint's answer, and the body sent. Resend sends one again with the same `webhook-id`.",
          },
          {
            type: "note",
            tone: "info",
            text: "If every delivery to an endpoint fails for 24 hours, the endpoint is switched off and the Developer page shows why. Fix your endpoint, then switch it back on.",
          },
        ],
      },
      {
        title: "Rotate the secret",
        blocks: [
          {
            type: "p",
            text: "Click the rotate icon next to the endpoint to get a new secret. For the next 24 hours each delivery is signed with both the old and the new secret, so you can update your receiver without missing events. After that only the new secret signs.",
          },
        ],
      },
    ],
    related: [
      { label: "Developer: API keys & webhooks", to: "/account/developer" },
      { label: "Screen settings: offline alerts", to: "/account/screen-settings" },
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
    ],
    keywords: ["webhook", "webhooks", "signature", "hmac", "standard webhooks", "webhook-signature", "verify", "retry", "retries", "screen.offline", "screen.online", "rotate secret"],
  },

  // ── Recipes ────────────────────────────────────────────────────────────────

  // ── On-site connector ──────────────────────────────────────────────────────
  {
    slug: "connector",
    category: "developers",
    route: "/developers/connector",
    group: "Recipes",
    title: "Read building systems with the PulsePanels Connector (BACnet, Modbus, MQTT)",
    summary: "Install a small connector on the building's network, find BACnet devices, Modbus registers or MQTT topics, and turn them into live data and triggers, with no code.",
    intro:
      "The PulsePanels Connector is a small service you run on a PC, VM or NUC on the building's network. It reads BACnet/IP points from the BMS, Modbus TCP registers from energy meters, PLCs and inverters, and MQTT topics from IoT sensors and gateways, and sends them to PulsePanels as live data, or fires a trigger when a point switches on. It only connects outward over HTTPS, so no firewall ports need opening, and it's set up entirely from the Developer page.",
    prerequisites: [
      "The API & integrations add-on on your organization.",
      "A machine on the building network with Docker (Linux recommended, 64-bit Intel/AMD or ARM such as a Raspberry Pi 4/5), or Python 3.10 or later.",
      "Outbound HTTPS from that machine to your PulsePanels address.",
      "BACnet: UDP port 47808 reachable on the BMS network. Modbus: TCP port 502 (or the device's port) reachable. MQTT: the broker reachable (1883, or 8883 with TLS).",
    ],
    readingTime: 8,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "Add the connector",
        blocks: [
          {
            type: "ol",
            items: [
              "Go to Account Settings → Developer → Connectors and click Add connector.",
              "Name it after the building or the system it reads, for example Tower A BMS. Limit it to a Space if its data and triggers should stay there.",
              "Copy the token and the install command. The token is shown once; you can always create a new one from the connector.",
            ],
          },
        ],
      },
      {
        title: "Install it on site",
        blocks: [
          { type: "p", text: "On the machine on the BMS network, run the command you copied. `--network host` is needed because BACnet discovery uses UDP broadcasts, which don't cross Docker's default network." },
          { type: "code", title: "Docker", lang: "bash", code: `docker run -d --name pulsepanels-connector --restart unless-stopped --network host \\
  -e PP_SERVER=https://app.pulsepanels.com \\
  -e PP_CONNECTOR_TOKEN=pcn_your_token \\
  pulsepanels/connector` },
          {
            type: "table",
            head: ["Setting", "When to set it"],
            rows: [
              ["`PP_BACNET_ADDRESS`", "The connector's address on the BMS network in CIDR form, e.g. `192.168.10.50/24`, if the machine has several network cards. Default: its main address, /24."],
              ["`PP_BACNET_DEVICE_ID`", "The connector's own BACnet device instance (default 599999). Change it if that number is already used on the site."],
              ["`PP_BACNET_BBMD`", "The BMS is on another subnet: register with its BBMD as a foreign device, e.g. `10.20.0.5`."],
              ["`PP_BACNET_ENABLED`", "`false` on a site with only Modbus or MQTT, so the connector doesn't open the BACnet port."],
              ["`PP_MQTT_URL`, `PP_MQTT_USERNAME`, `PP_MQTT_PASSWORD`", "The MQTT broker, set on site instead of in PulsePanels (for example to keep its password on site). These win over the broker set on the Developer page."],
            ],
          },
          { type: "p", text: "Within a minute the connector shows Online on the Developer page, with its host name and version." },
          { type: "note", tone: "info", text: "To update the connector, pull the new image and recreate the container with the same settings: `docker pull pulsepanels/connector`, then `docker rm -f pulsepanels-connector` and run the command again. If a connector is too old for a protocol, the Developer page says so." },
        ],
      },
      {
        title: "BACnet: scan and pick points",
        blocks: [
          {
            type: "ol",
            items: [
              "Open the connector (Points & scan), choose BACnet/IP and click Scan. It sends a BACnet Who-Is and lists every device that answers, with its points, current values and units. To scan one controller, enter its IP address first.",
              "Tick the points you want. Analog points feed live data: a key is suggested from the point's name, e.g. Lobby CO2 → `lobby.co2`, with the unit filled in.",
              "Binary points (a fire-panel input, a door contact) fire a trigger: pick the trigger. It fires while the point is active and stops when it goes inactive.",
              "Click Add points. The connector starts reading them within 30 seconds; each point shows its latest value, when it was read, and any read error.",
            ],
          },
          { type: "p", text: "Show the values with the Live Data app or `{{key}}` placeholders, and add rules (Live data → Rules) to fire triggers on thresholds." },
        ],
      },
      {
        title: "Modbus TCP: read registers",
        blocks: [
          { type: "p", text: "Modbus has no discovery, so you work from the device's register map (its manual lists which register holds what). The connector helps by reading a block of registers and showing them decoded." },
          {
            type: "ol",
            items: [
              "In Points & scan, choose Modbus TCP. Enter the device or gateway as `host[:port]` (port 502 by default) and its unit ID (1 for most devices; behind a Modbus gateway, the device's slave address).",
              "Choose the register type, the first register and how many to read, and how to read them: 16-bit integers, or 32/64-bit integers and floats. Click Read registers.",
              "Compare the values with the register map. If a 32-bit value looks wrong, switch the word order: many meters send the low word first.",
              "Tick the registers you want and add them, like BACnet points. Coils and discrete inputs (on/off) fire a trigger while they're true.",
            ],
          },
          {
            type: "table",
            head: ["In the manual", "In PulsePanels"],
            rows: [
              ["Holding register 40001 (or 4x0001)", "Holding register, offset 0"],
              ["Input register 30011", "Input register, offset 10"],
              ["Coil 00005", "Coil, offset 4"],
              ["32-bit float, CDAB / word-swapped", "float32, low word first"],
              ["Status bit 3 of register 40100", "Holding register, offset 99, bool, bit 3"],
            ],
          },
        ],
      },
      {
        title: "MQTT: set the broker and pick topics",
        blocks: [
          {
            type: "ol",
            items: [
              "In the connector, click Set broker and enter it as `mqtt://host:1883` (or `mqtts://host:8883` with TLS), with a username and password if it needs them. The password is stored encrypted and only sent to the connector. The status shows Connected once the connector is using it.",
              "In Points & scan, choose MQTT, enter the topics to listen to (`#` for everything, or `building/#`) and click Listen. The connector listens for a few seconds and lists every topic it saw, with its last message.",
              "For a JSON message, each field is listed separately, e.g. `co2` or `sensor.battery`: tick the fields you want. A plain message (`21.5`, `ON`) is used as a whole.",
              "Text values like ON / OFF or open / closed are suggested as triggers: set the value that means on.",
            ],
          },
          { type: "p", text: "MQTT points aren't polled: each message on the topic is a new reading. Messages the broker keeps (retained) arrive straight away when the connector subscribes." },
        ],
      },
      {
        title: "How it behaves",
        blocks: [
          {
            type: "ul",
            items: [
              "BACnet and Modbus points are read on their own interval (default every 15 seconds); MQTT points as their messages arrive. Only changes are sent: past the point's deadband for numbers, plus a keep-alive every 5 minutes.",
              "If PulsePanels can't be reached, the connector keeps the newest value of every point and sends them when the link is back.",
              "A read error (device offline, unknown object) is shown next to the point; the other points keep working.",
              "Points you add or change in PulsePanels are picked up at the next check-in, within 30 seconds. Nothing needs changing on site.",
            ],
          },
        ],
      },
      {
        title: "Troubleshooting",
        blocks: [
          {
            type: "table",
            head: ["What you see", "What to check"],
            rows: [
              ["Not connected yet", "The container is running (`docker logs pulsepanels-connector`) and the machine can reach your PulsePanels address over HTTPS."],
              ["`invalid_connector_token` in the log", "The token was regenerated or mistyped. Create a new token and restart the container with it."],
              ["The scan finds no devices", "The connector is on the BMS subnet (set `PP_BACNET_ADDRESS`), or the BMS is on another subnet: set `PP_BACNET_BBMD`, or scan one controller's IP."],
              ["`unknown-object` on a point", "The object type or instance doesn't exist on that device. Scan again and pick it from the list."],
              ["`no answer` on a point", "The device is offline or the address is wrong."],
              ["Modbus: `no connection to …`", "The device or gateway isn't reachable on that host and port from the connector's machine."],
              ["Modbus: `illegal data address`", "No such register on that device: check the register map and the offset (zero-based)."],
              ["Modbus: `gateway target didn't answer`", "The unit ID is wrong, or the device behind the gateway is offline."],
              ["Modbus: a value that's far off", "Wrong data type or word order: read the block again as another type, or with the low word first."],
              ["MQTT: Not connected", "The broker address, port, username or password. With mqtts:// and a self-signed certificate, tick Accept a self-signed certificate."],
              ["MQTT: `no field … in the payload`", "The JSON field name or path is wrong: listen again and pick it from the list."],
              ["MQTT: no value yet", "Nothing has been published on that topic since the connector subscribed."],
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Live data feeds API", to: "/developers/data-feeds-api" },
      { label: "Triggers API", to: "/developers/triggers-api" },
      { label: "Connect a building-management system", to: "/developers/connect-a-bms" },
    ],
    keywords: ["connector", "bacnet", "modbus", "modbus tcp", "mqtt", "broker", "mosquitto", "energy meter", "plc", "iot", "bms", "building management", "edge", "gateway", "on-site", "who-is", "scan", "register", "bbmd", "fire panel", "hvac", "sensors"],
  },
  {
    slug: "connect-a-bms",
    category: "developers",
    route: "/developers/connect-a-bms",
    group: "Recipes",
    title: "Connect a building-management system",
    summary: "Show every screen's status in your BMS, using your own point names, with a webhook or a one-minute poll.",
    intro:
      "A building-management system (BMS) can watch your screens like any other building equipment: an offline screen raises an alarm, and an operator sees it on the same dashboard as the lifts and air conditioning. This recipe connects the two using your BMS point names.",
    prerequisites: [
      "You are the Owner or an Admin in PulsePanels.",
      "Your BMS, or a small script next to it, can make HTTPS requests or receive them.",
    ],
    readingTime: 6,
    status: "published",
    lastUpdated: UPDATED,
    steps: [
      {
        title: "No code? Use the PulsePanels Connector",
        blocks: [
          { type: "p", text: "If the BMS speaks BACnet/IP, the meters Modbus TCP, or the sensors publish over MQTT, you don't need to write anything: install the PulsePanels Connector on the building network, find the points, and pick the ones to show or to fire triggers with. The steps below are for BMS teams that prefer to call the API themselves." },
          { type: "steps-ref", label: "Read building systems with the PulsePanels Connector", to: "/developers/connector" },
        ],
      },
      {
        title: "Give each screen its BMS point name",
        blocks: [
          {
            type: "p",
            text: "Open each screen in PulsePanels and set its External ID to the point name used in your BMS, for example `L3-LOBBY-01`. From then on your BMS never needs our IDs: it can ask for `/v1/screens/ext:L3-LOBBY-01`, and every screen in the API carries its `externalId`.",
          },
        ],
      },
      {
        title: "Create a key just for the BMS",
        blocks: [
          {
            type: "ol",
            items: [
              "Go to Account Settings → Developer and click New key.",
              "Name it after the system, for example Building management system.",
              "Under Access, tick only Read screens.",
              "Under Allowed IP addresses, enter the public address the BMS calls from.",
              "Create the key and store it in the BMS integration's settings.",
            ],
          },
        ],
      },
      {
        title: "Choose push or poll",
        blocks: [
          {
            type: "table",
            head: ["", "Webhooks (push)", "Polling"],
            rows: [
              ["How", "PulsePanels calls your endpoint on each change", "Your script calls `GET /v1/screens` every minute"],
              ["Needs", "An HTTPS endpoint reachable from the internet", "Outbound HTTPS only"],
              ["Best for", "Alarms the moment a screen drops", "BMS networks that can't accept incoming connections"],
            ],
          },
          {
            type: "p",
            text: "Many sites use both: a webhook for fast alarms, and a slower poll that corrects anything missed.",
          },
        ],
      },
      {
        title: "Option A: receive webhooks",
        blocks: [
          {
            type: "ol",
            items: [
              "Under Webhooks, click Add endpoint, enter your receiver's URL and keep both screen events ticked.",
              "In the receiver, verify the signature, look up the screen's `externalId` with `GET /v1/screens/{screenId}`, and set that BMS point to offline or online.",
              "Click Test and check the delivery shows Delivered.",
            ],
          },
          { type: "steps-ref", label: "Verification code for Node.js, Python and C#", to: "/developers/webhooks" },
        ],
      },
      {
        title: "Option B: poll every minute",
        blocks: [
          {
            type: "p",
            text: "A small script on the BMS server reads every screen once a minute and writes each one's `online` value to the point named by its `externalId`. One request covers 500 screens, well inside the limit of 300 requests a minute.",
          },
          { type: "code", title: "Python", lang: "python", code: BMS_POLLER },
        ],
      },
      {
        title: "Test the connection",
        blocks: [
          {
            type: "ul",
            items: [
              "Call `/v1/ping` from the BMS server. A `403 ip_not_allowed` means the key's allowed address doesn't match the address the server calls from.",
              "Unplug a test screen. After the offline time set in Screen settings, its BMS point should change.",
              "Plug it back in: it reports online again within a minute.",
            ],
          },
        ],
      },
      {
        title: "Put an evacuation message on screens when the fire alarm goes off",
        blocks: [
          {
            type: "p",
            text: "Give the BMS key the `emergency:trigger` scope. When the fire panel reports an alarm, the BMS sends an alert; when the panel is reset, it stops it.",
          },
          { type: "code", title: "Python", lang: "python", code: FIRE_ALARM },
          {
            type: "note",
            tone: "warning",
            text: "Test it outside opening hours with `\"template\": \"custom\", \"title\": \"TEST — no action needed\"` and a single screen as the target before you connect the real alarm.",
          },
          {
            type: "p",
            text: "Simpler still: set up a `fire-alarm` trigger on the Developer page with the alert and the screens, and have the BMS POST to its inbound URL on alarm and to `/stop` on reset. The message and targets then live in PulsePanels, and the BMS needs no API key.",
          },
          { type: "steps-ref", label: "Triggers API", to: "/developers/triggers-api" },
        ],
      },
      {
        title: "Show live readings on screens",
        blocks: [
          {
            type: "p",
            text: "Give the BMS key the `data:write` scope and send the points you want people to see, for example every 30 seconds in one request. Then add a Live Data app with those keys, or write `{{floor3.temp}}` in a Canvas design.",
          },
          { type: "code", title: "Request", lang: "bash", code: `curl -X POST ${BASE}/data -H "X-API-Key: pp_live_your_key" -H "Content-Type: application/json" \\
  -d '{"values": {"floor3.temp": 22.5, "floor3.co2": 840, "carpark.free": 41}}'` },
          { type: "steps-ref", label: "Live data feeds API", to: "/developers/data-feeds-api" },
        ],
      },
      {
        title: "Go further: zones and emergency alerts",
        blocks: [
          {
            type: "ul",
            items: [
              "Give each screen group the BMS zone name as its External ID, then read a whole zone with `GET /v1/groups/ext:<zone>`.",
              "Read `GET /v1/emergency/alerts/{id}` after sending: the BMS can confirm which screens displayed the alert, and flag the ones that didn't.",
              "Dim or mute screens out of hours with `PATCH /v1/screens/{id}`, and restart a misbehaving screen with `POST /v1/screens/{id}/commands`.",
              "Show each screen's software version and disk use from `GET /v1/screens/{id}/health`.",
            ],
          },
          { type: "steps-ref", label: "Spaces, screen groups & locations API", to: "/developers/organization-api" },
          { type: "steps-ref", label: "Emergency API: alerts & delivery", to: "/developers/emergency-api" },
        ],
      },
    ],
    related: [
      { label: "Screens API", to: "/developers/screens-api" },
      { label: "Webhooks: events, signatures & retries", to: "/developers/webhooks" },
      { label: "Authentication, keys & scopes", to: "/developers/authentication" },
    ],
    keywords: ["bms", "building management", "bacnet", "modbus", "niagara", "scada", "integration", "monitoring", "alarm", "external id", "point name", "poll", "live data", "trigger"],
  },
];
